'use client';

import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DocCodeSnippet } from '../types/docs.types';

interface DocsCodeBlockProps {
  snippet: DocCodeSnippet;
  className?: string;
}

export function DocsCodeBlock({ snippet, className }: DocsCodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    void navigator.clipboard.writeText(snippet.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'my-6 overflow-hidden rounded-2xl border border-pb-slate-200 bg-[#060c18] shadow-xl dark:border-pb-white/10',
        className
      )}
    >
      {/* Terminal Window Header Bar */}
      <div className="flex items-center justify-between border-b border-pb-slate-800 bg-[#0b1329] px-4 py-2.5 dark:border-pb-white/5 dark:bg-pb-white/[0.03]">
        <div className="flex items-center gap-2">
          {/* Terminal Dots */}
          <div className="flex gap-1.5 mr-2" aria-hidden="true">
            <div className="h-2.5 w-2.5 rounded-full bg-rose-500/80" />
            <div className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
            <div className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
          </div>
          {snippet.title ? (
            <span className="text-[12.5px] font-mono text-pb-slate-300 font-medium">
              {snippet.title}
            </span>
          ) : (
            <span className="text-[12px] font-mono text-pb-slate-400">code</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="rounded bg-pb-blue-500/10 px-2 py-0.5 font-mono text-[11px] font-semibold text-pb-blue-400">
            {snippet.language}
          </span>
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-pb-slate-400 hover:bg-pb-white/10 hover:text-pb-white transition-colors cursor-pointer"
          >
            {copied ? (
              <>
                <Check size={13} className="text-emerald-400" />
                <span className="text-emerald-400 text-[11px] font-medium">Copied</span>
              </>
            ) : (
              <>
                <Copy size={13} />
                <span className="text-[11px]">Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      {snippet.description && (
        <div className="border-b border-pb-slate-800/80 bg-pb-slate-900/40 px-4 py-2 text-xs text-pb-slate-400 dark:border-pb-white/5 dark:bg-pb-white/[0.02]">
          {snippet.description}
        </div>
      )}

      {/* Code body */}
      <div className="custom-scrollbar overflow-x-auto p-4 sm:p-5 font-mono text-[13px] leading-relaxed text-pb-slate-100">
        <pre className="text-pb-slate-100">
          <code>{snippet.code}</code>
        </pre>
      </div>
    </div>
  );
}
